import React, { useCallback, useEffect, useState } from "react";
import TurnrModal, { TurnrModalContainer, TurnrModalContent, TurnrModalFooter, TurnrModalSubTitle, TurnrModalTitle, useModalServiceTriggers } from "~/components/modals/TurnrModal.jsx";
import { TurnrActionAlert } from "~/components/alerts/TurnrAlert.jsx";
import { fetchFindShopOrder, fetchForeignOrder, performEntityAction } from "~/services";
import SearchInput from "~/components/input/inputs/SearchInput.jsx";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import { ServiceErrorAlert } from "~/components/Error.jsx";
import { getForeignOrderId, getOrderId } from "~/cores/orderCore";
import { TurnrCard } from "~/components/card/TurnrCard.jsx";
import SkeletonLoaderCard from "~/components/loading/skeletons/SkeletonLoaderCard.jsx";
import { TurnrTableBody, TurnrTableCell, TurnrTableHead, TurnrTableHeaderCell, TurnrTableHeaderRow, TurnrTableRow, TurnrTableSimple } from "~/components/table/TurnrTableSmart.jsx";
import AvatarAndItemName from "~/components/items/AvatarAndItemName.jsx";
import TurnrInput from "~/components/input/TurnrInput.jsx";
import TurnrForm from "~/components/input/TurnrForm.jsx";
import { SkeletonLoaderWrapper } from "~/components/loading/skeletons/SkeletonLoaderWrapper.jsx";
import ShipmentMethodSelect from "~/components/input/selects/ShipmentMethodSelect.jsx";
import ShopAddressSelect from "~/components/input/selects/ShopAddressSelect.jsx";
import { getSelectedBrandName } from "~/cores/appCore";
import ShopSelect from "~/components/input/selects/ShopSelect.jsx";
import { serviceIsLoading, useServiceHook } from "rylak-common";
import { ENTITY_ACTION_TYPE, ENTITY_TYPE } from "~/constants";
import { getShopEcomName } from "~/cores/ecom/ecomCore";

export default function CreateReturnModal(props) {
    const { appProps, open, onClose, onSuccess, order, defaultSearchValue = "", fixedShipmentMethodId = null } = props;

    const shopSelectionNeeded = !order && !getSelectedBrandName(appProps);

    const [selectedShop, setSelectedShop] = useState(null);
    const [searchValue, setSearchValue] = useState(defaultSearchValue);
    const [itemValues, setItemValues] = useState({});
    const [shipmentMethodId, setShipmentMethodId] = useState(fixedShipmentMethodId);
    const [returnShopAddressId, setReturnShopAddressId] = useState();
    const [returnHandlingCost, setReturnHandlingCost] = useState();
    const [returnShipmentCost, setReturnShipmentCost] = useState();

    let { serviceData: createReturnServiceData, fetchServiceData: createReturnFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: performEntityAction,
    });

    let {
        serviceData: fetchFindShopOrderServiceData,
        fetchServiceData: fetchFindShopOrderFn,
        clearServiceData: clearFindShopOrderFn,
    } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchFindShopOrder,
    });

    useEffect(() => {
        clearFindShopOrderFn();
    }, [selectedShop]);

    let { serviceData: foreignOrderServiceData, fetchServiceData: fetchForeignOrderService } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchForeignOrder,
    });

    const anyForeignOrderServiceData = order ? foreignOrderServiceData : fetchFindShopOrderServiceData;

    const onFindOrderClick = useCallback(() => {
        fetchFindShopOrderFn({
            merchantBrandId: selectedShop,
            foreignOrderId: searchValue,
        });
    }, [searchValue, selectedShop]);

    useEffect(() => {
        if (defaultSearchValue) {
            !shopSelectionNeeded && !order && onFindOrderClick();
        }
    }, [defaultSearchValue, order]);

    useEffect(() => {
        if (open && order) {
            fetchForeignOrderService({ orderId: getOrderId(order) });
        }
    }, [open, order]);

    useEffect(() => {
        setItemValues({});
    }, [fetchFindShopOrderServiceData]);

    function onSubmit() {
        createReturnFn({
            merchantBrandId: order?.merchantBrand?.id || selectedShop,
            entityType: ENTITY_TYPE.RETURN,
            action: {
                type: ENTITY_ACTION_TYPE.RETURN_CREATE,
                data: {
                    foreignOrderId: getForeignOrderId(order) || anyForeignOrderServiceData?.data?.foreignOrderId,
                    refundReturnShipmentCost: returnShipmentCost,
                    refundReturnHandlingCost: returnHandlingCost,
                    lineItems: Object.entries(itemValues)
                        .filter(([value]) => !!value)
                        .map(([foreignLineItemId, value]) => {
                            return {
                                foreignLineItemId: foreignLineItemId,
                                quantity: value,
                            };
                        }),
                    shipmentSolutionType: shipmentMethod?.type,
                    shipmentCarrier: shipmentMethod?.carrier,
                    returnShopAddress:
                        shipmentMethod?.requiresAddress && returnShopAddressId
                            ? {
                                  id: returnShopAddressId,
                              }
                            : null,
                },
            },
        });
    }

    const loadingFindOrder = serviceIsLoading(anyForeignOrderServiceData);
    const loadingCreateReturn = serviceIsLoading(createReturnServiceData);
    const loadingAnything = loadingFindOrder || loadingCreateReturn;
    const itemsWithQuantityLeft = anyForeignOrderServiceData?.data?.lineItems?.filter((lineItem) => lineItem?.quantityLeft > 0);

    function onItemValueChange(foreignLineItemId, value) {
        itemValues[foreignLineItemId] = value;
        setItemValues({ ...itemValues });
    }

    useModalServiceTriggers(createReturnServiceData, {
        appProps,
        onClose,
        onSuccess: () => onSuccess && onSuccess(createReturnServiceData, { orderId: searchValue }),
    });

    const returnShipment = anyForeignOrderServiceData?.data?.returnShipment;
    const shipmentMethod = returnShipment?.options?.find(({ id }) => id === shipmentMethodId);

    const content = (
        <>
            <TurnrModalContent>
                <TurnrGrid container spacing={2}>
                    <TurnrGrid item xs={12}>
                        <TurnrActionAlert fullWidth={true}>
                            Normally a customer will create a return through the return portal. However, if needed, you can create a return manually here
                        </TurnrActionAlert>
                    </TurnrGrid>
                    {!order && (
                        <TurnrGrid item xs={12}>
                            <TurnrGrid container spacing={1}>
                                <TurnrGrid item xs={12} sm={6}>
                                    <SearchInput
                                        label={"" + (appProps?.selectedBrandData ? getShopEcomName(appProps) + " order" : "Order")}
                                        fullWidth
                                        placeholder={"Foreign order ID"}
                                        value={searchValue}
                                        onChange={setSearchValue}
                                        disabled={loadingAnything}
                                    />
                                </TurnrGrid>
                                <TurnrGrid item xs={12} sm={6}>
                                    <TurnrButton fullWidth onClick={() => onFindOrderClick()} disabled={!searchValue || loadingAnything} loading={loadingFindOrder}>
                                        Find order
                                    </TurnrButton>
                                </TurnrGrid>
                            </TurnrGrid>
                        </TurnrGrid>
                    )}
                    {(order || loadingFindOrder || itemsWithQuantityLeft?.length > 0) && (
                        <TurnrGrid container spacing={2} item xs={12}>
                            <TurnrGrid item xs={12}>
                                <SkeletonLoaderWrapper loading={loadingAnything} slim>
                                    {!fixedShipmentMethodId && (
                                        <ShipmentMethodSelect
                                            appProps={appProps}
                                            value={shipmentMethodId}
                                            onChange={setShipmentMethodId}
                                            loading={loadingAnything}
                                            options={returnShipment?.options}
                                            style={{ minHeight: "55px" }}
                                        />
                                    )}

                                    {shipmentMethod?.requiresAddress && (
                                        <div style={{ marginTop: "16px" }}>
                                            <ShopAddressSelect
                                                appProps={appProps}
                                                required
                                                label="Return address"
                                                tooltip={"This is the address that returns will be shipped to"}
                                                disabled={loadingAnything}
                                                value={returnShopAddressId}
                                                onChange={setReturnShopAddressId}
                                                shopAddresses={returnShipment?.shopAddresses}
                                                style={{ minHeight: "55px" }}
                                            />
                                        </div>
                                    )}

                                    <TurnrGrid container spacing={2} style={{ marginTop: "16px" }}>
                                        <TurnrGrid item xs={12}>
                                            <TurnrModalSubTitle>Return Costs</TurnrModalSubTitle>
                                        </TurnrGrid>
                                        <TurnrGrid item xs={12}>
                                            <TurnrInput
                                                label={"Return handling cost"}
                                                value={returnHandlingCost}
                                                type={"number"}
                                                min={0}
                                                disabled={loadingAnything}
                                                fullWidth={true}
                                                onChange={setReturnHandlingCost}
                                                tooltip={"This handling cost will be subtracted from the customer's refund amount"}
                                            />
                                        </TurnrGrid>
                                        <TurnrGrid item xs={12}>
                                            <TurnrInput
                                                label={"Return shipment cost"}
                                                value={returnShipmentCost}
                                                type={"number"}
                                                min={0}
                                                fullWidth={true}
                                                disabled={loadingAnything}
                                                onChange={setReturnShipmentCost}
                                                tooltip={"This shipping cost will be subtracted from the customer's refund amount"}
                                            />
                                        </TurnrGrid>
                                    </TurnrGrid>
                                </SkeletonLoaderWrapper>
                            </TurnrGrid>
                        </TurnrGrid>
                    )}
                </TurnrGrid>

                <ServiceErrorAlert className={"mt2"} noMargins={true} serviceData={anyForeignOrderServiceData} appProps={appProps} />
            </TurnrModalContent>
            <TurnrModalContent>
                {loadingFindOrder && (
                    <TurnrCard style={{ height: "150px" }}>
                        <SkeletonLoaderCard />
                    </TurnrCard>
                )}
                {itemsWithQuantityLeft?.length === 0 && <TurnrActionAlert>All items in this order has already been added to a return</TurnrActionAlert>}
            </TurnrModalContent>

            {itemsWithQuantityLeft?.length > 0 && (
                <TurnrTableSimple>
                    <TurnrTableHead>
                        <TurnrTableHeaderRow>
                            <TurnrTableHeaderCell>Item</TurnrTableHeaderCell>
                            <TurnrTableHeaderCell>Quantity</TurnrTableHeaderCell>
                        </TurnrTableHeaderRow>
                    </TurnrTableHead>
                    <TurnrTableBody>
                        {itemsWithQuantityLeft?.map((lineItem) => {
                            return (
                                <TurnrTableRow key={lineItem?.item?.foreignLineItemId}>
                                    <TurnrTableCell item container direction={"row"} xs={12}>
                                        <AvatarAndItemName productVariant={lineItem?.productVariant} shopLink={false} productLink={false} />
                                    </TurnrTableCell>
                                    <TurnrTableCell>
                                        <TurnrInput
                                            size={"small"}
                                            label={"" + lineItem?.quantityLeft + " available"}
                                            type={"number"}
                                            min={0}
                                            step={1}
                                            disabled={loadingCreateReturn}
                                            max={lineItem?.quantityLeft}
                                            value={itemValues[lineItem?.item?.foreignLineItemId] || 0}
                                            onChange={(v) => onItemValueChange(lineItem?.item?.foreignLineItemId, v)}
                                        />
                                    </TurnrTableCell>
                                </TurnrTableRow>
                            );
                        })}
                    </TurnrTableBody>
                </TurnrTableSimple>
            )}
        </>
    );

    return (
        <TurnrModal open={open} onClose={onClose}>
            <TurnrModalContainer>
                <TurnrForm onSubmit={onSubmit}>
                    <div style={{ maxWidth: "500px" }}>
                        <TurnrModalTitle>Create Return</TurnrModalTitle>

                        {shopSelectionNeeded && (
                            <TurnrModalContent>
                                <ShopSelect appProps={appProps} value={selectedShop} onChange={setSelectedShop} />
                            </TurnrModalContent>
                        )}
                        {(order || selectedShop || getSelectedBrandName(appProps)) && content}

                        <TurnrModalFooter onCancel={onClose} disabled={!(itemsWithQuantityLeft?.length > 0 || order)} loading={loadingCreateReturn} />
                    </div>
                </TurnrForm>
            </TurnrModalContainer>
        </TurnrModal>
    );
}
